import { useContext, useState } from "react";
import styled from "styled-components";
import { CognitoContext } from "../../contexts/cognito/cognito-provider";
import { CurrentCompanyStorage } from "../../library/core/current-company";
import { VoucherTypes } from "../../library/core/enums";
import { FormatDecimal } from "../../library/core/formats";
import { Alert, Alerts } from "../bootstrap/alert";
import { CompanySelectInput } from "../form/company-dropdown";
import { PrimaryButton } from "../form/primary-button";
import { PageContainer } from "../layout/page-container";
import { ToggleView } from "../views/toggle-view";
import { RedeemCertificateVoucher } from "./redeem-certificate-voucher";
import { RedeemGiftCardVoucher } from "./redeem-giftcard-voucher";
import { RedemptionSuccessMessage } from "./redemption-success";
import { CaptureTotalBillAmount } from "./total-bill-capture";
import { TotalBillPreview } from "./total-bill-preview";
import { PullVoucherType } from "./voucher-type";

const RedemptionStep = {
  CaptureTotalBillAmount: 1,
  CaptureNewVoucher: 2,
  RedeemVoucher: 3,
  RedemptionSuccess: 4,
  RedemptionComplete: 5,
};

const SpanBlock = styled.span`
  display: inline-block;
  padding: 0 3px;
`;

const LabeledAmount = ({ label, amount, digits }) => {
  digits = digits || 2;
  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <div className="form-control" style={{ fontWeight: 400 }}>
        <SpanBlock>${FormatDecimal(amount, digits)}</SpanBlock>
      </div>
    </div>
  );
};

export const RedeemVoucher = ({
  dataModel,
  serviceDispatcher,
  companyOptions,
  onTrxCompleted,
  onNewTransaction,
  onUnauthorised,
}) => {
  const { state, dispatch } = useContext(CognitoContext);

  const [formState, setFormState] = useState({
    isProcessing: false,
    showRemainingAmount: false,
    totalBillAmount: 0,
    transactionGroupId: null,
    totalRedeemed: 0,
    isSubmitted: false,
    isSuccess: false,
    errorMessage: null,
    step: RedemptionStep.CaptureTotalBillAmount,
  });

  const [message, setMessage] = useState({
    showMessage: false,
    isSuccess: false,
    errorMessage: null,
    cardNumber: null,
    totalRedeemed: 0,
    totalBillAmount: null,
    transactionGroupId: null,
  });

  const [model, setModel] = useState(() => {
    const storedCompanyId = CurrentCompanyStorage.getCurrentCompanyId();

    return (
      dataModel || {
        totalBillAmount: 0,
        totalRedeemed: 0,
        companyId: Number(storedCompanyId) || companyOptions.defaultCompanyId,
      }
    );
  });

  const [errors, setErrors] = useState({});

  const onSetVoucher = (voucher) => {
    setMessage({});

    setFormState({
      ...formState,
      voucher,
      step: RedemptionStep.RedeemVoucher,
    });

    setModel({
      ...model,
      voucher: voucher,
      cardNumber: voucher.VoucherNumber,
    });
  };

  const onSetTargetCompany = (companyId) => {
    CurrentCompanyStorage.saveCurrentCompanyId(companyId);

    setModel({
      ...model,
      companyId,
    });
  };

  const onSetTotalBillAmount = ({ totalBillAmount }) => {
    if (totalBillAmount <= 0) return;
    if (typeof window !== 'undefined') window.__hideSideMenu = true;
    setModel({
      ...model,
      totalBillAmount,
    });

    setFormState({
      ...formState,
      step: RedemptionStep.CaptureNewVoucher,
    });
  };

  const onUnsetVoucher = () => {
    setMessage({});

    setFormState({
      ...formState,
      voucher: null,
      step: RedemptionStep.CaptureNewVoucher,
    });

    setModel({
      ...model,
      voucher: null,
      cardNumber: null,
    });
  };

  const onUseAnotherVoucher = (event) => {
    event.preventDefault();
    onUnsetVoucher();
    setMessage({});
  };

  const onRedeemSuccess = (props) => {
    const { voucherKind, voucherNumber, redeemedAmount, transactionGroupId } =
      props;

    let step = RedemptionStep.RedemptionComplete;

    setModel({
      ...model,
      totalRedeemed: formState.totalRedeemed + redeemedAmount,
    });

    setFormState({
      ...formState,
      step,
      voucherKind,
      voucherNumber,
      redeemedAmount: redeemedAmount,
      transactionGroupId: transactionGroupId,
      totalRedeemed: formState.totalRedeemed + redeemedAmount,
    });

    setMessage({
      showRedemptionMessage: true,
      isSuccess: true,
    });

    onTrxCompleted && onTrxCompleted(props);
  };

  const onError = ({ errorMessage }) => {
    setMessage({
      errorMessage,
      showMessage: true,
      isError: true,
    });
  };

  const onClearForm = () => {
    setModel({
      totalBillAmount: 0,
      totalRedeemed: 0,
      companyId: Number(CurrentCompanyStorage.getCurrentCompanyId()) || model.companyId,
    });
    setMessage({});
    setErrors({});
    setFormState({
      totalBillAmount: 0,
      transactionGroupId: null,
      totalRedeemed: 0,
      companyId: Number(CurrentCompanyStorage.getCurrentCompanyId()) || model.companyId,
      step: RedemptionStep.CaptureTotalBillAmount,
    });
    if (typeof window !== 'undefined') window.__hideSideMenu = false;
    onNewTransaction && onNewTransaction();
  };

  const newTrxButton = (
    <PrimaryButton className="mx-auto mt-5" onClick={onClearForm}>
      Done
    </PrimaryButton>
  );

  const startNewButton = (
    <ToggleView show={true || formState.transactionGroupId}>
      <ToggleView show={model.totalBillAmount > formState.totalRedeemed}>
        <span style={{ display: "inline-block", padding: "0 10px" }}>Or</span>
      </ToggleView>
      {newTrxButton}
    </ToggleView>
  );

  const otherButtons = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onUseAnotherVoucher}
      >
        Use another gift
        {(formState.voucher && formState.voucher.VoucherKind) ===
          VoucherTypes.GiftCard
          ? " card"
          : " certificate"}
      </button>
      {startNewButton}
    </>
  );

  const otherPanels = <></>;

  return (
    <PageContainer>
      <div>
        <ToggleView
          show={formState.step === RedemptionStep.CaptureTotalBillAmount}
        >
          <CompanySelectInput
            dataModel={model}
            {...companyOptions}
            customClassName="fixed left-1/2 -translate-x-1/2 top-4 z-50 !w-[min(640px,calc(100vw-2rem))] !rounded-xl "
            onChange={onSetTargetCompany}
          />
        </ToggleView>

        <ToggleView
          show={formState.step === RedemptionStep.CaptureTotalBillAmount}
        >

          <CaptureTotalBillAmount onSetTotalBillAmount={onSetTotalBillAmount} />
        </ToggleView>

        <ToggleView show={message.showMessage}>
          <Alert
            type={Alerts.danger}
            hide={!message.isError || !message.errorMessage}
          >
            {message.errorMessage}
          </Alert>
        </ToggleView>

        <ToggleView show={model.totalBillAmount > 0}>
          <TotalBillPreview
            totalBillAmount={model.totalBillAmount}
            totalRedeemed={formState.totalRedeemed}
            voucher={model.voucher}
            onClearForm={onClearForm}
            model={model}
            companyOptions={companyOptions}
          />
        </ToggleView>

        <ToggleView show={formState.step === RedemptionStep.RedemptionComplete}>
          <RedemptionSuccessMessage
            showMessage={
              message.showRedemptionMessage &&
              model.totalBillAmount > 0 &&
              message.isSuccess
            }
            redeemedAmount={formState.redeemedAmount}
            onUseAnotherVoucher={onUseAnotherVoucher}
            dataModel={model}
            newTrxButton={newTrxButton}
            totalBillAmount={model.totalBillAmount}
            totalRedeemed={formState.totalRedeemed}
          />
        </ToggleView>

        <ToggleView
          show={
            !(
              formState.totalRedeemed > 0 &&
              formState.totalRedeemed >= model.totalBillAmount
            ) && formState.step === RedemptionStep.CaptureNewVoucher
          }
        >
          <PullVoucherType
            enableBarcodeScanning={true}
            dataModel={model}
            onSetVoucher={onSetVoucher}
            otherPanels={otherPanels}
            otherButtons={startNewButton}
            serviceDispatcher={serviceDispatcher}
          />
        </ToggleView>

        <ToggleView
          show={
            formState.voucher && formState.step === RedemptionStep.RedeemVoucher
          }
        >
          {!formState.voucher ? null : formState.voucher.VoucherKind ===
            VoucherTypes.GiftCard ? (
            <RedeemGiftCardVoucher
              dataModel={model}
              transactionGroupId={formState.transactionGroupId}
              onRedeemSuccess={onRedeemSuccess}
              onError={onError}
              onUnauthorised={onUnauthorised}
              otherPanels={otherPanels}
              otherButtons={otherButtons}
              onUseAnotherVoucher={onUseAnotherVoucher}
              serviceDispatcher={serviceDispatcher}
            />
          ) : (
            <RedeemCertificateVoucher
              onRedeemSuccess={onRedeemSuccess}
              dataModel={model}
              transactionGroupId={formState.transactionGroupId}
              onError={onError}
              onUnauthorised={onUnauthorised}
              otherPanels={otherPanels}
              otherButtons={otherButtons}
              serviceDispatcher={serviceDispatcher}
              onUseAnotherVoucher={onUseAnotherVoucher}
            />
          )}
        </ToggleView>
      </div>
    </PageContainer>
  );
};
